import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown, X } from 'lucide-react';

type VenueFilterSelectProps = {
  label: string;
  options: Array<{ key: string; label: string }>;
  value: string[];
  onChange: (value: string[]) => void;
  placeholder: string;
  loading?: boolean;
  disabled?: boolean;
  hint?: string;
  /** Visual step in a cascade (1, 2, 3). */
  step?: number;
};

type MenuStyle = CSSProperties;

function isSelected(value: string[], option: { key: string; label: string }) {
  return value.includes(option.key) || value.includes(option.label);
}

function buildMenuStyle(trigger: HTMLElement): MenuStyle {
  const rect = trigger.getBoundingClientRect();
  const gap = 8;
  const pad = 12;
  const width = Math.min(
    Math.max(rect.width, 300),
    window.innerWidth - pad * 2,
  );
  const maxMenu = 320;
  const spaceBelow = window.innerHeight - rect.bottom - gap - pad;
  const spaceAbove = rect.top - gap - pad;
  const openDown = spaceBelow >= 160 || spaceBelow >= spaceAbove;
  const left = Math.min(
    Math.max(pad, rect.left),
    window.innerWidth - width - pad,
  );

  const base: MenuStyle = {
    position: 'fixed',
    left,
    width,
    zIndex: 1100,
    maxHeight: Math.min(
      maxMenu,
      openDown ? Math.max(160, spaceBelow) : Math.max(160, spaceAbove),
    ),
  };

  if (openDown) return { ...base, top: rect.bottom + gap };
  return { ...base, bottom: window.innerHeight - rect.top + gap };
}

export function VenueFilterSelect({
  label,
  options,
  value,
  onChange,
  placeholder,
  loading,
  disabled,
  hint,
  step,
}: VenueFilterSelectProps) {
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState<MenuStyle | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const listId = useId();
  const isDisabled = disabled || loading;

  const selectedOptions = options.filter((option) => isSelected(value, option));

  const triggerLabel = loading
    ? 'Loading…'
    : selectedOptions.length === 0
      ? placeholder
      : selectedOptions.length === 1
        ? selectedOptions[0].label
        : `${selectedOptions.length} selected`;

  const syncMenuPosition = () => {
    if (!triggerRef.current) return;
    setMenuStyle(buildMenuStyle(triggerRef.current));
  };

  useLayoutEffect(() => {
    if (!open) {
      setMenuStyle(null);
      return;
    }
    syncMenuPosition();
  }, [open, options.length, selectedOptions.length]);

  useEffect(() => {
    if (!open) return;
    const onReposition = () => syncMenuPosition();
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node;
      if (rootRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  useEffect(() => {
    if (isDisabled) setOpen(false);
  }, [isDisabled]);

  const toggle = (option: { key: string; label: string }) => {
    const selected = isSelected(value, option);
    if (selected) {
      onChange(
        value.filter((item) => item !== option.key && item !== option.label),
      );
      return;
    }
    onChange([...value, option.key]);
  };

  const menu =
    open && !isDisabled && menuStyle && typeof document !== 'undefined'
      ? createPortal(
          <div
            ref={menuRef}
            id={listId}
            role="listbox"
            aria-multiselectable="true"
            aria-label={label}
            style={menuStyle}
            className="flex flex-col overflow-hidden rounded-[14px] border border-black/[0.08] bg-white shadow-[0_18px_48px_-16px_rgba(16,38,73,0.4)]"
          >
            <div className="flex items-center justify-between border-b border-black/[0.05] px-3 py-2">
              <span className="text-[11px] font-semibold text-[var(--dash-muted)]">
                {label}
              </span>
              {selectedOptions.length > 0 ? (
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => onChange([])}
                  className="text-[11px] font-semibold text-[var(--dash-brand)]"
                >
                  Clear
                </button>
              ) : null}
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-1.5">
              {options.length === 0 ? (
                <p className="px-3 py-4 text-center text-[12px] text-[var(--dash-muted)]">
                  No options available
                </p>
              ) : (
                options.map((option) => {
                  const selected = isSelected(value, option);
                  return (
                    <button
                      key={option.key}
                      type="button"
                      role="option"
                      aria-selected={selected}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => toggle(option)}
                      className={`mb-0.5 flex w-full items-start gap-2.5 rounded-[10px] px-2.5 py-2.5 text-left transition ${
                        selected
                          ? 'bg-[var(--dash-brand)]/[0.08]'
                          : 'hover:bg-black/[0.035]'
                      }`}
                    >
                      <span
                        className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border ${
                          selected
                            ? 'border-[var(--dash-brand)] bg-[var(--dash-brand)] text-white'
                            : 'border-[#D5DBE5] bg-white'
                        }`}
                      >
                        {selected ? (
                          <Check className="h-3 w-3" strokeWidth={3} />
                        ) : null}
                      </span>
                      <span className="min-w-0 flex-1 text-[13px] font-medium leading-snug text-[var(--dash-ink)]">
                        {option.label}
                      </span>
                    </button>
                  );
                })
              )}
            </div>
            {selectedOptions.length > 0 ? (
              <div className="border-t border-black/[0.05] bg-[#FAFBFC] px-3 py-2 text-[11px] font-medium text-[var(--dash-muted)]">
                {selectedOptions.length} selected · tap outside to close
              </div>
            ) : null}
          </div>,
          document.body,
        )
      : null;

  return (
    <div ref={rootRef} className="text-left">
      <div className="mb-1.5 flex items-center gap-2">
        {step != null ? (
          <span
            className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold ${
              selectedOptions.length > 0
                ? 'bg-[var(--dash-brand)] text-white'
                : isDisabled
                  ? 'bg-[#EEF1F5] text-[#9AA8B8]'
                  : 'bg-[#EEF1F5] text-[var(--dash-ink)]'
            }`}
          >
            {step}
          </span>
        ) : null}
        <label className="text-[12px] font-semibold tracking-[-0.01em] text-[var(--dash-ink)]">
          {label}
        </label>
      </div>
      {hint ? (
        <p className="m-0 mb-1.5 text-[11px] leading-snug text-[var(--dash-muted)]">
          {hint}
        </p>
      ) : null}
      <button
        ref={triggerRef}
        type="button"
        disabled={isDisabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => setOpen((current) => !current)}
        className={`flex h-11 w-full items-center justify-between gap-2 rounded-[14px] border px-3 text-left text-[13px] outline-none transition ${
          isDisabled
            ? 'cursor-not-allowed border-black/[0.06] bg-[#F7F8FA] text-[var(--dash-muted)]'
            : open
              ? 'cursor-pointer border-[var(--dash-brand)]/40 bg-white shadow-[0_0_0_3px_rgba(41,121,255,0.12)]'
              : 'cursor-pointer border-black/[0.08] bg-white hover:border-black/[0.14]'
        }`}
      >
        <span
          className={`min-w-0 truncate font-medium ${
            selectedOptions.length
              ? 'text-[var(--dash-ink)]'
              : 'text-[#8E98A8]'
          }`}
        >
          {triggerLabel}
        </span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 transition ${
            open ? 'rotate-180 text-[var(--dash-brand)]' : 'text-[#9AA8B8]'
          }`}
        />
      </button>
      {selectedOptions.length > 0 ? (
        <div className="mt-1.5 flex flex-wrap gap-1">
          {selectedOptions.map((option) => (
            <span
              key={option.key}
              className="inline-flex max-w-full items-center gap-1 rounded-full bg-[#F2F4F7] py-1 pr-1 pl-2.5 text-[11px] font-semibold text-[var(--dash-ink)]"
            >
              <span className="truncate">{option.label}</span>
              <button
                type="button"
                aria-label={`Remove ${option.label}`}
                onClick={() => toggle(option)}
                className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[var(--dash-muted)] hover:bg-white hover:text-[var(--dash-ink)]"
              >
                <X className="h-2.5 w-2.5" />
              </button>
            </span>
          ))}
        </div>
      ) : null}
      {menu}
    </div>
  );
}
